import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
  createDefaultZone,
  MediaFieldValue,
  NetworkDetailsFormValues,
  NetworkNodeFieldPath,
} from '../../../types';
import { getMediaFieldPreviewUrl } from '../../../../../buildOnBoardingDraftPayload';
import AppTextInput from '../../../../../../../components/common/AppTextInput';
import AppMUIAutocomplete from '../../../../../../../components/common/AppMUIAutoComplete';
import { ZONE_COLORS } from '../../../../../../users/data';
import { SVG } from '../../../../../../../constants/assetLists';
import { getZones } from './defaultZone';
import AppButton from '../../../../../../../components/common/AppButton';
import { getInitials } from '../../../../../../../utils';

type ZoneDrawFieldProps = {
  logo: MediaFieldValue;
  inputId: string;
  onLogoChange: (logo: MediaFieldValue) => void;
};

function ZoneDrawField({ logo, inputId, onLogoChange }: ZoneDrawFieldProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!logo) {
      setPreviewUrl(null);
      return;
    }

    const mediaUrl = getMediaFieldPreviewUrl(logo);
    if (mediaUrl) {
      setPreviewUrl(mediaUrl);
      return;
    }

    if (logo instanceof File) {
      const objectUrl = URL.createObjectURL(logo);
      setPreviewUrl(objectUrl);

      return () => URL.revokeObjectURL(objectUrl);
    }
  }, [logo]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    onLogoChange(file);
    e.target.value = '';
  };

  const hasLogo = Boolean(logo && previewUrl);

  return (
    <div className={`w-full h-full`}>
      <input
        ref={fileInputRef}
        id={inputId}
        type="file"
        hidden
        accept="image/png, image/jpeg, image/jpg"
        onChange={handleFileChange}
      />

      {hasLogo ? (
        <div className="flex flex-col gap-3">
          <img
            src={previewUrl!}
            alt="לוגו הסניף"
            className="max-w-full max-h-[300px] object-contain"
          />

          <div className="flex items-center gap-2">
            <AppButton
              iconStart={
                <img
                  src={SVG.imageIcon}
                  alt="edit"
                  className="w-[15px] h-[15px] object-contain"
                />
              }
              onClick={() => fileInputRef.current?.click()}
            >
              החלפת תמונה
            </AppButton>
            <span className="text-base text-textColor">
              יש להעלות קבצים בפורמט PNG, JPG בלבד.
            </span>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <div
            className={`relative w-[202px] h-[116px] rounded-lg border border-gray90 bg-gray90 flex items-center justify-center overflow-hidden`}
          >
            <label
              htmlFor={inputId}
              className="absolute inset-0 flex items-center justify-center cursor-pointer"
            >
              <div className="w-[80px] h-[80px] pointer-events-none">
                <img
                  src={SVG.imgPlaceholderWhiteIcon}
                  alt="icon"
                  className="w-full h-full object-contain"
                />
              </div>

              <span className="absolute left-50% top-50% transform -translate-x-50% -translate-y-50% text-base text-textColor pointer-events-none text-center">
                העלאת שרטוט <br /> אזורים
              </span>
            </label>
          </div>

          <span className="w-[147px] text-base text-textColor">
            יש להעלות קבצים בפורמט PNG, JPG בלבד.
          </span>
        </div>
      )}
    </div>
  );
}

type Props = {
  selectedFieldPath: NetworkNodeFieldPath;
};

function Zones({ selectedFieldPath }: Props) {
  const { control, setValue } = useFormContext<NetworkDetailsFormValues>();

  const zoneDrawingInputId = `${selectedFieldPath}.zoneDrawing`;

  const didInitRef = useRef(false);

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: `${selectedFieldPath}.zones`,
    keyName: 'id',
  });

  useEffect(() => {
    if (didInitRef.current) return;

    if (fields.length === 0) {
      replace(getZones());
    }

    didInitRef.current = true;
  }, [fields.length, replace]);

  const handleAddZone = () => {
    append(createDefaultZone());
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 ">
      <div className="grid grid-cols-12 gap-10">
        <div
          className={`col-span-8 flex flex-col gap-3 ${fields.length == 0 ? 'flex-1' : ''}`}
        >
          {fields.map((field, index) => (
            <div key={field.id} className="flex gap-3">
              <span className="text-base text-textColor font-semibold self-center w-[15px]">
                {index + 1}
              </span>

              <Controller
                control={control}
                name={`${selectedFieldPath}.zones.${index}.name`}
                render={({ field, fieldState: { error } }) => (
                  <div className="w-[400px]">
                    <AppTextInput
                      label="שם האזור"
                      placeholder="שם האזור"
                      className="border-none"
                      required
                      {...field}
                      onChange={(e) => {
                        const name = e.target.value;

                        setValue(
                          `${selectedFieldPath}.zones.${index}.name`,
                          name,
                          { shouldDirty: true },
                        );
                        setValue(
                          `${selectedFieldPath}.zones.${index}.shortName`,
                          getInitials(name),
                        );
                      }}
                      error={error?.message}
                    />
                  </div>
                )}
              />

              <Controller
                control={control}
                name={`${selectedFieldPath}.zones.${index}.shortName`}
                render={({ field, fieldState: { error } }) => (
                  <div className="w-[400px]">
                    <AppTextInput
                      label="שם מקוצר "
                      placeholder="שם מקוצר "
                      className="border-none"
                      required
                      {...field}
                      error={error?.message}
                    />
                  </div>
                )}
              />

              <Controller
                name={`${selectedFieldPath}.zones.${index}.color`}
                control={control}
                render={({ field: colorField, fieldState: { error } }) => (
                  <div className="w-[149px]">
                    <AppMUIAutocomplete
                      label="צבע"
                      required
                      size="small"
                      options={ZONE_COLORS}
                      fullWidth
                      getOptionLabel={(o) => o.color}
                      slotProps={{
                        listbox: {
                          sx: {
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 1,
                            p: 1,
                            maxHeight: 320,
                            overflowY: 'auto',
                            '& .MuiAutocomplete-option': {
                              p: '8px',
                              '&.Mui-focused, &[aria-selected="true"]': {
                                backgroundColor: 'transparent !important',
                              },
                            },
                          },
                        },
                      }}
                      renderValue={(value) =>
                        value && (
                          <div className="flex items-center gap-2">
                            <div
                              className="w-[24px] h-[24px] rounded-[3px] border-1 border-gray"
                              style={{ backgroundColor: value?.color }}
                            />
                            <span className="text-base text-textColor">
                              צבע
                            </span>
                          </div>
                        )
                      }
                      renderOption={(props, option) => {
                        const { key, ...optionProps } = props;
                        const isSelected = colorField.value === option?.color;

                        return (
                          <li key={key} {...optionProps}>
                            <div
                              className="w-[24px] h-[24px] rounded-[3px]"
                              style={{
                                backgroundColor: option?.color,
                                border: isSelected
                                  ? '2px solid #333333'
                                  : '1px solid #333333',
                              }}
                            />
                          </li>
                        );
                      }}
                      value={
                        ZONE_COLORS.find(
                          (item) => item?.color === colorField.value,
                        ) || null
                      }
                      onChange={(_event, option) =>
                        colorField.onChange(option?.color ?? '')
                      }
                      onBlur={colorField.onBlur}
                      error={!!error?.message}
                      helperText={error?.message}
                    />
                  </div>
                )}
              />

              <button
                type="button"
                className="w-8 h-8 border border-gray rounded-lg flex items-center justify-center self-center shadow-sm"
                onClick={() => {
                  remove(index);
                }}
              >
                <img
                  src={SVG.deleteGrayIcon}
                  alt="del"
                  className="w-4 h-[18px] object-contain"
                />
              </button>
            </div>
          ))}

          <div>
            <button
              type="button"
              className="w-8 h-8 border border-gray rounded-lg flex items-center justify-center self-center shadow-sm"
              onClick={handleAddZone}
            >
              <img
                src={SVG.addDarkBlueIcon}
                alt="add"
                className="w-[14px] h-[14px] object-contain"
              />
            </button>
          </div>
        </div>

        <div className={`col-span-4`}>
          <Controller
            control={control}
            name={`${selectedFieldPath}.zoneDrawing`}
            render={({ field }) => (
              <ZoneDrawField
                logo={field.value}
                inputId={zoneDrawingInputId}
                onLogoChange={field.onChange}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
}

export default Zones;
